import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Notification {
  id: string
  type: 'client_message' | 'session_reminder' | 'payment_received' | 'goal_achieved' | 'system' | 'workout_completed' | 'check_in_overdue'
  title: string
  message: string
  timestamp: Date
  read: boolean
  actionUrl?: string
  clientId?: string
  clientName?: string
  priority: 'low' | 'medium' | 'high'
  avatar?: string
}

interface NotificationStore {
  // State
  notifications: Notification[]
  unreadCount: number
  isOpen: boolean
  
  // Actions
  setNotifications: (notifications: Notification[]) => void
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void
  markAsRead: (notificationId: string) => void
  markAllAsRead: () => void
  removeNotification: (notificationId: string) => void
  clearAllNotifications: () => void
  loadMockNotifications: () => void
  setIsOpen: (isOpen: boolean) => void
  
  // Computed
  getUnreadNotifications: () => Notification[]
  getNotificationsByType: (type: Notification['type']) => Notification[]
  updateUnreadCount: () => void
}

// Mock notifications data
const generateMockNotifications = (): Notification[] => [
  {
    id: '1',
    type: 'client_message',
    title: 'New message from Sarah Johnson',
    message: 'Hey coach! I have a question about my nutrition plan...',
    timestamp: new Date(Date.now() - 5 * 60 * 1000), // 5 minutes ago
    read: false,
    actionUrl: '/messages',
    clientId: '1',
    clientName: 'Sarah Johnson',
    priority: 'medium',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=32&h=32&fit=crop&crop=face'
  },
  {
    id: '2',
    type: 'session_reminder',
    title: 'Upcoming session with Mike Chen',
    message: 'Training session starts in 30 minutes',
    timestamp: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
    read: false,
    actionUrl: '/schedule',
    clientId: '2',
    clientName: 'Mike Chen',
    priority: 'high',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face'
  },
  {
    id: '3',
    type: 'goal_achieved',
    title: 'Emma Wilson reached a milestone!',
    message: 'Completed her first 5K run goal 🎉',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    read: true,
    actionUrl: '/clients/3',
    clientId: '3',
    clientName: 'Emma Wilson',
    priority: 'medium',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=32&h=32&fit=crop&crop=face'
  },
  {
    id: '4',
    type: 'workout_completed',
    title: 'Alex Turner completed workout',
    message: 'Upper Body Strength - Day 3 completed with great form!',
    timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
    read: true,
    actionUrl: '/clients/4',
    clientId: '4',
    clientName: 'Alex Turner',
    priority: 'low',
    avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=32&h=32&fit=crop&crop=face'
  },
  {
    id: '5',
    type: 'check_in_overdue',
    title: 'Check-in overdue: Lisa Garcia',
    message: 'No check-in received for 3 days',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
    read: false,
    actionUrl: '/clients/5',
    clientId: '5',
    clientName: 'Lisa Garcia',
    priority: 'high',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=32&h=32&fit=crop&crop=face'
  },
  {
    id: '6',
    type: 'payment_received',
    title: 'Payment received',
    message: 'Monthly subscription payment from David Kim',
    timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
    read: true,
    actionUrl: '/billing',
    clientId: '6',
    clientName: 'David Kim',
    priority: 'low'
  },
  {
    id: '7',
    type: 'system',
    title: 'Weekly report available',
    message: 'Your weekly coaching summary is ready to view',
    timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
    read: true,
    actionUrl: '/analytics',
    priority: 'low'
  }
]

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      // Initial state
      notifications: [],
      unreadCount: 0,
      isOpen: false,
      
      // Actions
      setNotifications: (notifications) => {
        set({ notifications })
        get().updateUnreadCount()
      },
      
      addNotification: (notificationData) => {
        const newNotification: Notification = {
          ...notificationData,
          id: Date.now().toString(),
          timestamp: new Date(),
        }
        
        set(state => ({
          notifications: [newNotification, ...state.notifications]
        }))
        get().updateUnreadCount()
      },
      
      markAsRead: (notificationId) => {
        set(state => ({
          notifications: state.notifications.map(notification =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification
          )
        }))
        get().updateUnreadCount()
      },
      
      markAllAsRead: () => {
        set(state => ({
          notifications: state.notifications.map(notification => ({
            ...notification,
            read: true
          }))
        }))
        get().updateUnreadCount()
      },
      
      removeNotification: (notificationId) => {
        set(state => ({
          notifications: state.notifications.filter(
            notification => notification.id !== notificationId
          )
        }))
        get().updateUnreadCount()
      },
      
      clearAllNotifications: () => {
        set({ notifications: [] })
        get().updateUnreadCount()
      },
      
      loadMockNotifications: () => {
        get().setNotifications(generateMockNotifications())
      },
      
      setIsOpen: (isOpen) => set({ isOpen }),
      
      // Computed functions
      getUnreadNotifications: () => {
        return get().notifications.filter(notification => !notification.read)
      },
      
      getNotificationsByType: (type) => {
        return get().notifications.filter(notification => notification.type === type)
      },
      
      updateUnreadCount: () => {
        const unreadCount = get().notifications.filter(notification => !notification.read).length
        set({ unreadCount })
      }
    }),
    {
      name: 'notification-store',
      partialize: (state) => ({
        notifications: state.notifications,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // If no notifications exist after rehydration, load mock data
          if (state.notifications.length === 0) {
            state.setNotifications(generateMockNotifications())
          }
          state.updateUnreadCount()
        }
      },
    }
  )
)

// Initialize store with mock data if no persisted data exists
const initializeStore = () => {
  const state = useNotificationStore.getState()
  if (state.notifications.length === 0) {
    state.setNotifications(generateMockNotifications())
  }
  state.updateUnreadCount()
}

// For development: Clear localStorage and load fresh data
// You can remove this line after testing
if (typeof window !== 'undefined') {
  localStorage.removeItem('notification-store')
}

// Initialize on store creation
setTimeout(initializeStore, 100) // Small delay to ensure store is ready